import {
  TeButtonOutline,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeText,
} from '@topcon/tuicl-react';
import styled from 'styled-components';
import { useTranslation } from '@topcon/topcon-utils';
import { use, useState } from 'react';

import { AddressInfo, RequestBusinessPartnerBody } from '../../../api/generated/pm';
import { BusinessPartnerCard } from '../../BusinessPartnerCard';
import EditBusinessPartnerModal from '../../Modal/EditBusinessPartnerModal';
import { ReviewCompanyCreationRequestStepActions } from './ReviewCompanyCreationRequestStepActions';
import { ReviewResultContextType } from '../../../providers/ReviewResultContext/review-result.types';
import { ReviewResultContext } from '../../../providers/ReviewResultContext/review-result.context';

interface ReviewCompanyCreationRequestInfoProps {
  requestBusinessPartner?: RequestBusinessPartnerBody;
  visible: boolean;
  onRefetch: () => void;
  onNextStep: () => void;
  id: string;
}

function ReviewCompanyCreationRequestStep({
  visible,
  requestBusinessPartner,
  onRefetch,
  id,
  onNextStep,
}: ReviewCompanyCreationRequestInfoProps) {
  const { t } = useTranslation();
  const [openEditBusinessPartnerModal, setOpenEditBusinessPartnerModal] = useState<boolean>(false);
  const { setResult } = use<ReviewResultContextType>(ReviewResultContext);

  if (!visible || !requestBusinessPartner) return;

  const handleCloseEditBPModal = (shouldUpdate: boolean) => {
    setOpenEditBusinessPartnerModal(false);
    if (shouldUpdate) {
      onRefetch();
    }
  };

  const handleReject = () => {
    setResult('declined');
  };

  return (
    <>
      <TeContainer justifyContent="space-between" fill padding={0} gap={0} column>
        <TeContainerSlim column fill padding={['m']} gap="xs2">
          <TeText weight={700} size={20}>
            {t('companyReview')}
          </TeText>
          <TeText color="text.secondary" size={14}>
            {t('serviceAgreement.request.description')}
          </TeText>
        </TeContainerSlim>
        <TeDivider dividerColor="border.secondary" />
        <TeContainerSlim fill grow padding="m" gap="xs" alignItems="center">
          <TeContainerSlimWrapper column fill grow gap="xs">
            <BusinessPartnerCard
              badgeLabel={t('dealer')}
              name={requestBusinessPartner?.provider?.name ?? ''}
              subName={t('businessCompany')}
              address={requestBusinessPartner?.provider?.companyAddress as AddressInfo}
            />
          </TeContainerSlimWrapper>
          <TeContainerSlimWrapper column fill grow gap="xs">
            <BusinessPartnerCard
              badgeLabel={t('customer')}
              name={requestBusinessPartner?.name ?? ''}
              subName={t('businessCompany')}
              address={requestBusinessPartner?.companyAddress as AddressInfo}
            />

            <TeContainer
              backgroundColor="background.tertiary"
              padding={['xl', 'l']}
              fill
              grow
              column
              borderRadius="m"
              gap="m"
            >
              <TeContainerSlim column gap="xs" fill grow>
                <TeText size={14} weight={700}>
                  {t('wrongCompanyDetails')}
                </TeText>
                <TeText size={12} weight={400}>
                  {t('wrongCompanyDetailsDescription')}
                </TeText>
                <TeButtonOutline
                  text={t('generalInformation.editDetails', { ns: 'settings' })}
                  size="s"
                  teclick={() => setOpenEditBusinessPartnerModal(true)}
                />
              </TeContainerSlim>
            </TeContainer>
          </TeContainerSlimWrapper>
        </TeContainerSlim>
        <TeDivider dividerColor="border.secondary" />
        {openEditBusinessPartnerModal && (
          <EditBusinessPartnerModal
            id={id}
            open={openEditBusinessPartnerModal}
            onClose={handleCloseEditBPModal}
          />
        )}
        <ReviewCompanyCreationRequestStepActions
          step="companyReview"
          id={id}
          onNextStep={onNextStep}
          onReject={handleReject}
        />
      </TeContainer>
    </>
  );
}

const TeContainerSlimWrapper = styled(TeContainerSlim)<{ $span?: boolean }>`
  width: 100% !important;
  height: 100% !important;
`;

export { ReviewCompanyCreationRequestStep };
